// Authentik OIDC: Authorization Code flow with PKCE (oidc-client-ts default for `code`).
import {UserManager, WebStorageStateStore} from 'oidc-client-ts';
import config from '../config';
import {endFirstPartySession, getLegacyToken, getSession} from './legacy';

let manager = null;

export const callbackPath = '/auth/callback';

export const getUserManager = () => {
  if (!manager) {
    const origin = window.location.origin;
    manager = new UserManager({
      authority: config.oidcAuthority,
      client_id: config.oidcClientId,
      redirect_uri: `${origin}${callbackPath}`,
      post_logout_redirect_uri: `${origin}/landing`,
      response_type: 'code',
      scope: config.oidcScope,
      automaticSilentRenew: true,
      // Tokens live in sessionStorage (per tab, cleared on close), never localStorage.
      userStore: new WebStorageStateStore({store: window.sessionStorage}),
      stateStore: new WebStorageStateStore({store: window.sessionStorage}),
    });
  }
  return manager;
};

export const getAccessToken = async () => {
  const legacy = getLegacyToken();
  if (legacy) return legacy;
  const user = await getUserManager().getUser();
  if (!user || user.expired) return null;
  return user.access_token;
};

export const hasLegacySession = () => getSession() !== null;

export const login = (returnTo = '/') => getUserManager().signinRedirect({state: {returnTo}});

// Set when an Authentik sign-in completes. The API client reads it: a token rejected right after
// a fresh sign-in (for example, a deactivated account) stops the redirect loop instead of sending
// the browser back to the identity provider again.
const SIGNED_IN_KEY = 'aidb.auth.signedInAt';
const FRESH_SIGN_IN_MS = 60_000;

const sessionStore = () => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

export const completeLogin = async () => {
  const user = await getUserManager().signinRedirectCallback();
  try {
    sessionStore()?.setItem(SIGNED_IN_KEY, String(Date.now()));
  } catch {
    // Storage refused the write: the loop guard is best effort.
  }
  return (user.state && user.state.returnTo) || '/';
};

/** True for a short time after an Authentik sign-in completed in this tab. */
export const signedInRecently = () => {
  try {
    const at = Number(sessionStore()?.getItem(SIGNED_IN_KEY));
    return Number.isFinite(at) && at > 0 && Date.now() - at < FRESH_SIGN_IN_MS;
  } catch {
    return false;
  }
};

/** Forgets the fresh-sign-in mark (the token has been accepted, or the loop was stopped). */
export const clearSignedIn = () => {
  try {
    sessionStore()?.removeItem(SIGNED_IN_KEY);
  } catch {
    // Nothing to clear.
  }
};

/**
 * Drops the OIDC session in this tab without contacting Authentik. Used when the API rejects a
 * freshly completed Authentik sign-in (for example a deactivated account) so the app stops
 * redirecting back to the identity provider.
 */
export const forgetIdentitySession = async () => {
  try {
    await getUserManager().removeUser();
  } catch {
    // Storage unavailable: nothing to drop.
  }
};

/**
 * Signs out. First-party session: revoke the token server-side, then always clear the local
 * session and leave. Authentik session: end the IdP session through its end-session endpoint.
 * The logout endpoint is NOT called here: for Authentik tokens it is a 204 no-op on the backend,
 * so it would add a request without ending anything. The IdP end-session is what ends it.
 */
export const logout = async () => {
  if (getSession()) {
    await endFirstPartySession();
    window.location.assign('/landing');
    return;
  }
  const mgr = getUserManager();
  const user = await mgr.getUser();
  await mgr.removeUser();
  await mgr.signoutRedirect({id_token_hint: user ? user.id_token : undefined});
};
